import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@noor/ui';

/**
 * The cancel-an-order dialog, reproducing the old panel's confirm sheet.
 *
 * This exists because cancelling is IRREVERSIBLE and the backend records the
 * reason. The old screen offers seven checkbox reasons plus a free-text field
 * and keeps its submit button DISABLED until at least one of the two is
 * filled; an earlier version of this port fired `cancelOrder` with an empty
 * reason on a single click, which killed a live delivery with no confirmation
 * and no explanation attached.
 *
 * The reason string is assembled exactly as the original did —
 * `selectedReasons.join(',') + ', ' + freeText` — so what lands in the order
 * log reads the same as it always has, trailing comma and all.
 */

/** The seven fixed reasons, in the old `reasons` array's order. */
const REASON_KEYS = [
  'low_scooter_battery',
  'not_moving_in_my_direction',
  'rude_courier',
  'long_wait_time',
  'cannot_contact_courier',
  'courier_asked_to_cancel',
  'delivery_no',
] as const;

interface LegacyCancelOrderDialogProps {
  open: boolean;
  /** Shown in the title, as `Отменить заказ #1234`. */
  orderNumber?: string;
  isPending?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export function LegacyCancelOrderDialog(props: LegacyCancelOrderDialogProps) {
  // Remounted per order, which is what clears the picked reasons: carrying a
  // previous order's reasons into the next cancellation would attach the wrong
  // explanation to a live delivery. A keyed remount rather than a reset effect,
  // which `react-hooks/set-state-in-effect` rightly rejects.
  /*
   * Nothing renders while closed. Radix keeps a closing dialog mounted until
   * its exit animation reports `animationend`, which inside `.legacy-root`
   * never arrives — the sheet stays visible with its overlay blocking the page
   * (see `legacy-confirm-dialog` for the observed detail). Unmounting also
   * makes the keyed remount below redundant for the closed case, though the
   * key still matters when switching directly between two orders.
   */
  if (!props.open) return null;

  return <CancelDialogBody key={props.orderNumber ?? 'open'} {...props} />;
}

function CancelDialogBody({
  orderNumber,
  isPending = false,
  onConfirm,
  onCancel,
}: LegacyCancelOrderDialogProps) {
  const { t } = useTranslation('legacy');
  const [selected, setSelected] = useState<string[]>([]);
  const [freeText, setFreeText] = useState('');

  const toggle = (label: string) => {
    setSelected((current) =>
      current.includes(label) ? current.filter((r) => r !== label) : [...current, label],
    );
  };

  // The original's guard: a checkbox OR free text, never neither.
  const canSubmit = selected.length > 0 || freeText.trim() !== '';

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !isPending) onCancel();
      }}
    >
      <DialogContent
        data-legacy
        showClose={false}
        // White sheet, not `.legacy-root`'s page grey (see `legacy-sheet`).
        // Keep every control reachable when seven localized reasons wrap on a
        // short screen. The content scrolls inside the viewport-sized sheet.
        className="legacy-root legacy-sheet flex max-h-[calc(100dvh-2rem)] max-w-[686px] flex-col gap-0 overflow-y-auto rounded-[24px] border-0 p-4 sm:max-w-[686px] sm:p-6"
      >
        <div className="flex min-h-0 flex-col items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            aria-label={t('cancel.dismiss')}
            className="absolute top-4 right-4 size-10 cursor-pointer disabled:opacity-50"
          >
            <img src="/legacy-cancel/close.svg" alt="" className="size-10" />
          </button>

          <img
            src="/legacy-cancel/warning.png"
            alt=""
            aria-hidden="true"
            className="size-12 shrink-0"
          />

          <DialogTitle className="shrink-0 text-center text-[24px] leading-7 font-bold tracking-[-0.96px] text-[#1e1e1e]">
            {t('cancel.cancel_order_title')}
            {orderNumber ? ` #${orderNumber}` : ''}
          </DialogTitle>

          <DialogDescription className="shrink-0 text-center text-[16px] leading-[normal] font-medium tracking-[-0.32px] text-[var(--lg-grey-400)]">
            {t('cancel.cancel_order_desc')}
          </DialogDescription>

          {/* Reasons. Labels are the TRANSLATED strings, because the old app
              sent `t(item)` — the reason text stored on the order is whatever
              language the merchant was using. */}
          <div className="flex w-full shrink-0 flex-col items-start gap-2 text-left">
            {REASON_KEYS.map((key) => {
              const label = t(`cancel.reasons.${key}`);
              const checked = selected.includes(label);
              return (
                <label
                  key={key}
                  className={`flex cursor-pointer items-center gap-2 text-[16px] tracking-[-0.32px] ${
                    checked ? 'font-medium text-black' : 'font-normal text-[#181b21]'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(label)}
                    disabled={isPending}
                    className="peer sr-only"
                  />
                  <img
                    src={
                      checked
                        ? '/legacy-cancel/checkbox-checked.svg'
                        : '/legacy-cancel/checkbox.svg'
                    }
                    alt=""
                    aria-hidden="true"
                    className="size-6 shrink-0 rounded-full peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--lg-orange-500)]"
                  />
                  {label}
                </label>
              );
            })}
          </div>

          <textarea
            value={freeText}
            onChange={(event) => setFreeText(event.target.value)}
            disabled={isPending}
            placeholder={t('cancel.cancel_reason')}
            aria-label={t('cancel.cancel_reason')}
            className="min-h-16 w-full resize-y rounded-2xl border border-solid border-[#e6e9f0] bg-white px-4 py-3 text-[16px] leading-[1.25] text-[var(--lg-fg)] outline-none placeholder:text-[#74767a] focus:border-[var(--lg-orange-500)]"
          />

          <button
            type="button"
            // Disabled until a reason exists — the old dialog's own rule, and
            // the whole point of this screen.
            disabled={isPending || !canSubmit}
            onClick={() => {
              onConfirm(`${selected.join(',')}, ${freeText}`);
            }}
            className="h-12 w-full shrink-0 cursor-pointer rounded-[96px] bg-[var(--lg-orange-500)] p-4 text-[16px] leading-[1.25] font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('cancel.confirm')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
